import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="branding-preview"
export default class extends Controller {
  static targets = [
    "primaryPicker", "primaryText",
    "secondaryPicker", "secondaryText",
    "accentPicker", "accentText",
    "sectionTextPicker", "sectionTextText",
    "componentBgPicker", "componentBgText",
    "colorPreview", "previewHeading", "previewText",
    "previewCard", "previewCardText", "previewButton",
    // Image targets
    "logoPreview", "logoPlaceholder", "logoCurrent",
    "faviconPreview", "faviconPlaceholder", "faviconCurrent",
    "heroPreview", "heroPreviewWrapper", "heroPlaceholder", "heroCurrent",
    "bannerPreview", "bannerPreviewWrapper", "bannerPlaceholder", "bannerCurrent",
    "stripePreview", "stripePreviewWrapper", "stripePlaceholder", "stripeCurrent",
    "galleryPreviewContainer", "galleryPlaceholder", "galleryCurrent", "galleryCount"
  ]

  connect() {
    // Controller connected
  }

  // ========================================
  // Color Preview Methods
  // ========================================

  // Color picker changed - update text input and preview
  updateFromPicker(event) {
    const picker = event.target
    const colorType = picker.dataset.colorType
    const textTarget = this[`${colorType}TextTarget`]

    if (textTarget) {
      textTarget.value = picker.value
    }

    this.updateColorPreview()
  }

  // Text input changed - update color picker and preview
  updateFromText(event) {
    const textInput = event.target
    const colorType = textInput.dataset.colorType
    const value = textInput.value

    // Only update if valid hex color
    if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
      const pickerTarget = this[`${colorType}PickerTarget`]
      if (pickerTarget) {
        pickerTarget.value = value
      }
      this.updateColorPreview()
    }
  }

  updateColorPreview() {
    const primary = this.hasPrimaryPickerTarget ? this.primaryPickerTarget.value : '#1A1A1A'
    const secondary = this.hasSecondaryPickerTarget ? this.secondaryPickerTarget.value : '#F5E4A8'
    const accent = this.hasAccentPickerTarget ? this.accentPickerTarget.value : '#d4af37'
    const sectionText = this.hasSectionTextPickerTarget ? this.sectionTextPickerTarget.value : '#4f3a00'
    const componentBg = this.hasComponentBgPickerTarget ? this.componentBgPickerTarget.value : '#FFFFFF'

    // Update preview elements
    if (this.hasColorPreviewTarget) {
      this.colorPreviewTarget.style.backgroundColor = secondary
    }
    if (this.hasPreviewHeadingTarget) {
      this.previewHeadingTarget.style.color = primary
    }
    if (this.hasPreviewTextTarget) {
      this.previewTextTarget.style.color = sectionText
    }
    if (this.hasPreviewCardTarget) {
      this.previewCardTarget.style.backgroundColor = componentBg
    }
    if (this.hasPreviewCardTextTarget) {
      this.previewCardTextTarget.style.color = primary
    }
    if (this.hasPreviewButtonTarget) {
      this.previewButtonTarget.style.backgroundColor = accent
    }
  }

  // ========================================
  // Image Preview Methods
  // ========================================

  previewLogo(event) {
    this.previewImage(event, {
      preview: this.hasLogoPreviewTarget ? this.logoPreviewTarget : null,
      placeholder: this.hasLogoPlaceholderTarget ? this.logoPlaceholderTarget : null,
      current: this.hasLogoCurrentTarget ? this.logoCurrentTarget : null
    })
  }

  previewFavicon(event) {
    this.previewImage(event, {
      preview: this.hasFaviconPreviewTarget ? this.faviconPreviewTarget : null,
      placeholder: this.hasFaviconPlaceholderTarget ? this.faviconPlaceholderTarget : null,
      current: this.hasFaviconCurrentTarget ? this.faviconCurrentTarget : null
    })
  }

  previewHero(event) {
    this.previewImage(event, {
      preview: this.hasHeroPreviewTarget ? this.heroPreviewTarget : null,
      wrapper: this.hasHeroPreviewWrapperTarget ? this.heroPreviewWrapperTarget : null,
      placeholder: this.hasHeroPlaceholderTarget ? this.heroPlaceholderTarget : null,
      current: this.hasHeroCurrentTarget ? this.heroCurrentTarget : null
    })
  }

  previewBanner(event) {
    this.previewImage(event, {
      preview: this.hasBannerPreviewTarget ? this.bannerPreviewTarget : null,
      wrapper: this.hasBannerPreviewWrapperTarget ? this.bannerPreviewWrapperTarget : null,
      placeholder: this.hasBannerPlaceholderTarget ? this.bannerPlaceholderTarget : null,
      current: this.hasBannerCurrentTarget ? this.bannerCurrentTarget : null
    })
  }

  previewStripe(event) {
    this.previewImage(event, {
      preview: this.hasStripePreviewTarget ? this.stripePreviewTarget : null,
      wrapper: this.hasStripePreviewWrapperTarget ? this.stripePreviewWrapperTarget : null,
      placeholder: this.hasStripePlaceholderTarget ? this.stripePlaceholderTarget : null,
      current: this.hasStripeCurrentTarget ? this.stripeCurrentTarget : null
    })
  }

  previewImage(event, { preview, wrapper, placeholder, current }) {
    const file = event.target.files[0]
    if (!file || !file.type.startsWith('image/')) return

    if (preview) {
      const reader = new FileReader()
      reader.onload = (e) => {
        preview.src = e.target.result
        preview.classList.remove('hidden')
        if (wrapper) wrapper.classList.remove('hidden')
        if (placeholder) placeholder.classList.add('hidden')
        if (current) current.classList.add('hidden')
      }
      reader.readAsDataURL(file)
    }
  }

  // Gallery preview - handles multiple files
  previewGallery(event) {
    const files = event.target.files
    if (!files || files.length === 0) return

    const container = this.hasGalleryPreviewContainerTarget ? this.galleryPreviewContainerTarget : null
    if (!container) return

    // Clear existing previews
    container.innerHTML = ''

    // Hide placeholder, current, and count if they exist
    if (this.hasGalleryPlaceholderTarget) {
      this.galleryPlaceholderTarget.classList.add('hidden')
    }
    if (this.hasGalleryCurrentTarget) {
      this.galleryCurrentTarget.classList.add('hidden')
    }
    if (this.hasGalleryCountTarget) {
      this.galleryCountTarget.classList.add('hidden')
    }

    // Show container
    container.classList.remove('hidden')

    // Create preview for each file
    Array.from(files).forEach((file, index) => {
      if (!file.type.startsWith('image/')) return

      const reader = new FileReader()
      reader.onload = (e) => {
        const div = document.createElement('div')
        div.className = 'relative group'
        div.innerHTML = `
          <img src="${e.target.result}" class="w-full h-32 object-cover rounded-lg border-2 border-green-400" alt="Gallery preview ${index + 1}" />
          <div class="absolute top-1 right-1">
            <span class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">New</span>
          </div>
        `
        container.appendChild(div)
      }
      reader.readAsDataURL(file)
    })
  }
}
